import {
  AppProvider,
  UserLoginProvider,
  MemberRegisterProvider,
  MemberUserNameProvider,
  ForgetPasswordProvider,
  ForgotUserNameProvider,
  PreferenceDetailsProvider,
  MySSOUrlProvider,
  MyPlanProvider,
  MemberIDCardProvider,
  CardsPdfProvider,
  MemberIDRequestProvider,
  MyDeleteProvider,
  MemberServiceProvider,
  DocumentLinksProvider,
  BenefitsProvider,
  MyConsolidatedProvider,
  AuthorizationDepedentProvider,
  MyDepedentAccessProvider,
  AuthorizationDetailsProvider,
  CoveredEligibleProvider,
  PlanDocumentProvider,
  MyPlanSOBProvider,
  MyPlanEOCProvider,
  GetDependentAccessProvider,
  MedicalClaimEOBProvider,
  MedicalClaimDetailsProvider,
  MedicalClaimTaxSummaryProvider,
  MemberIDFaxProvider,
  MedicalClaimListProvider,
  ContactusProvider,
  MyFeatureAttributeProvider,
  MyHomeTilesProvider,
  UpdatePlanProvider,
  ChattestProvider,
  FeaturePermissionProvider,
  VirtualCareCategoryProvider,
  MemberEligibilityProvider,
  SecretQuestionProvider,
  UpdatePasswordProvider,
} from '@sentara-health/sentara-api-hooks-core';
import {
  DeviceTypeProvider,
  IdleSurveyModal,
  isIdleSurveyDismissed,
  getDecryptedValue,
  UserDetails,
  deleteEncryptedValue,
  NavigateConst,
  setLocalStorageBoolean,
} from 'common';
import { BrandingProvider, GlobalContext } from '../context';
import { useContext, useEffect, useState } from 'react';
import CmsContentLoader from './components/CmsContentLoader';
import { useSessionTimer } from './hooks/useSessionTimer';
import { stopOpenReplaySession } from 'common/openreplay/openreplay';

// Runs the session timer inside the AppProvider tree so useRefreshToken can read HttpContext
const SessionTimerRunner = () => {
  const { getAuthToken, userEncodedData, } =
    useContext(GlobalContext);
  const signOutAndAlert = async () => {
    const savedLanguage = localStorage.getItem('userLanguagePreference');
    localStorage.clear();
    if (savedLanguage) {
      localStorage.setItem('userLanguagePreference', savedLanguage);
    }
    deleteEncryptedValue();
    await stopOpenReplaySession();
    window.location.href = NavigateConst.signIn;
    setLocalStorageBoolean(UserDetails?.inActiveSignOut, true);
  };
  const isLoggedIn =
    Boolean(getAuthToken) &&
    userEncodedData?.length > 0 &&
    JSON.parse(userEncodedData)?.key !== 'anonymous';
    useSessionTimer({
      isLoggedIn, authToken: getAuthToken, onTimeout: () => {
        // Sign out the user when the session timer expires
        return signOutAndAlert();
    }
  });
  return null;
};

export const AppWrapper = ({
  header,
  authToken,
  xAuthToken,
  xSentaraTokenID,
  children,
}: {
  header: Record<string, string | undefined>;
  authToken: string;
  xAuthToken: string;
  xSentaraTokenID: string;
  children: React.ReactNode;
}) => {
  const { getFeature, getAuthToken, personId, userEncodedData } =
    useContext(GlobalContext);
  const [showIdleSurveyModal, setShowIdleSurveyModal] = useState(false);
  const isCSATEnabled = Boolean(getFeature?.CSATSurvey);
  useEffect(() => {
    let isMounted = true;

    const checkIdleSurveyEligibility = async () => {
      const isLoggedIn =
        Boolean(getAuthToken) &&
        userEncodedData?.length > 0 &&
        JSON.parse(userEncodedData)?.key !== 'anonymous';

      if (!isLoggedIn) {
        if (isMounted) {
          setShowIdleSurveyModal(false);
        }
        return;
      }
      if (!isCSATEnabled) {
        if (isMounted) {
          setShowIdleSurveyModal(false);
        }
        return;
      }

      const storedUserId = (await getDecryptedValue(UserDetails.id)) ?? '';
      const dismissed = await isIdleSurveyDismissed(storedUserId || '');

      if (isMounted) {
        setShowIdleSurveyModal(!dismissed);
      }
    };

    checkIdleSurveyEligibility();

    return () => {
      isMounted = false;
    };
  }, [getAuthToken, userEncodedData, personId, isCSATEnabled]);

  return (
    <AppProvider
      config={{
        httpConfig: {
          baseURL: process.env.REACT_APP_BASE_URL,
          authToken: authToken || '',
          commonHeaders: header,
          CIAMEnabled: getFeature?.isModernizedAPI ?? false,
          modBaseURL: process.env.REACT_APP_MOD_BASE_URL,
          memberProductSubscriptionKey:
            process.env.REACT_APP_MEMBER_PRODUCT_SUBSCRIPTION_KEY,
          planDocumentsProductSubscriptionKey:
            process.env.REACT_APP_PLAN_DOCUMENTS_PRODUCT_SUBSCRIPTION_KEY,
          identificationCardApiProductSubscriptionKey:
            process.env.REACT_APP_IDENTIFICATION_CARD_PRODUCT_SUBSCRIPTION_KEY,
          guestProductSubscriptionKey:
            process.env.REACT_APP_GUEST_PRODUCT_SUBSCRIPTION_KEY,
          personProductSubscriptionKey:
            process.env.REACT_APP_PERSON_PRODUCT_SUBSCRIPTION_KEY,
          proxyProductSubscriptionKey:
            process.env.REACT_APP_PROXY_PRODUCT_SUBSCRIPTION_KEY,
          pingBaseURL: process.env.REACT_APP_PING_BASE_URL,
          pingToken: process.env.REACT_APP_PING_TOKEN ?? '',
          virtualCareCategoryProductSubscriptionKey:
            process.env
              .REACT_APP_VIRTUAL_CARE_CATEGORY_PRODUCT_SUBSCRIPTION_KEY,
          xAuthToken: xAuthToken || '',
          xSentaraTokenID: xSentaraTokenID || '',
          preferencesProductSubscriptionKey:
            process.env.REACT_APP_PREFERENCES_PRODUCT_SUBSCRIPTION_KEY,
          claimFHIRProductSubscriptionKey:
            process.env.REACT_APP_SENTARA_FHIR_API_PRODUCT_SUBSCRIPTION_KEY,
          referralRequestProductSubscriptionKey:
            process.env.REACT_APP_REFERRAL_REQUEST_PRODUCT_SUBSCRIPTION_KEY,
          cmsSiteCoreProductSubscriptionKey:
            process.env.REACT_APP_CMS_SITECORE_PRODUCT_SUBSCRIPTION_KEY,
        },
      }}
    >
      <CmsContentLoader />
      <UserLoginProvider>
        <ChattestProvider>
          <ForgetPasswordProvider>
            <MyHomeTilesProvider>
              <AuthorizationDetailsProvider>
                <ForgotUserNameProvider>
                  <AuthorizationDepedentProvider>
                    <MemberRegisterProvider>
                      <MyDepedentAccessProvider>
                        <DocumentLinksProvider>
                          <MemberUserNameProvider>
                            <MyPlanProvider>
                              <MyConsolidatedProvider>
                                <BenefitsProvider>
                                  <MySSOUrlProvider>
                                    <MemberIDCardProvider>
                                      <CardsPdfProvider>
                                        <MemberIDRequestProvider>
                                          <PreferenceDetailsProvider>
                                            <MyDeleteProvider>
                                              <MemberServiceProvider>
                                                <CoveredEligibleProvider>
                                                  <PlanDocumentProvider>
                                                    <MyPlanSOBProvider>
                                                      <MyPlanEOCProvider>
                                                        <MedicalClaimTaxSummaryProvider>
                                                          <MemberIDFaxProvider>
                                                            <GetDependentAccessProvider>
                                                              <MedicalClaimEOBProvider>
                                                                <MedicalClaimDetailsProvider>
                                                                  <MedicalClaimListProvider>
                                                                    <ContactusProvider>
                                                                      <MyFeatureAttributeProvider>
                                                                        <DeviceTypeProvider>
                                                                          <UpdatePlanProvider>
                                                                            <SecretQuestionProvider>
                                                                              <UpdatePasswordProvider>
                                                                                <VirtualCareCategoryProvider>
                                                                                  <MemberEligibilityProvider>
                                                                                    <FeaturePermissionProvider>
                                                                                      <BrandingProvider>
                                                                                        <SessionTimerRunner />
                                                                                        {
                                                                                          children
                                                                                        }
                                                                                        {showIdleSurveyModal && (
                                                                                          <IdleSurveyModal />
                                                                                        )}
                                                                                      </BrandingProvider>
                                                                                    </FeaturePermissionProvider>
                                                                                  </MemberEligibilityProvider>
                                                                                </VirtualCareCategoryProvider>
                                                                              </UpdatePasswordProvider>
                                                                            </SecretQuestionProvider>
                                                                          </UpdatePlanProvider>
                                                                        </DeviceTypeProvider>
                                                                      </MyFeatureAttributeProvider>
                                                                    </ContactusProvider>
                                                                  </MedicalClaimListProvider>
                                                                </MedicalClaimDetailsProvider>
                                                              </MedicalClaimEOBProvider>
                                                            </GetDependentAccessProvider>
                                                          </MemberIDFaxProvider>
                                                        </MedicalClaimTaxSummaryProvider>
                                                      </MyPlanEOCProvider>
                                                    </MyPlanSOBProvider>
                                                  </PlanDocumentProvider>
                                                </CoveredEligibleProvider>
                                              </MemberServiceProvider>
                                            </MyDeleteProvider>
                                          </PreferenceDetailsProvider>
                                        </MemberIDRequestProvider>
                                      </CardsPdfProvider>
                                    </MemberIDCardProvider>
                                  </MySSOUrlProvider>
                                </BenefitsProvider>
                              </MyConsolidatedProvider>
                            </MyPlanProvider>
                          </MemberUserNameProvider>
                        </DocumentLinksProvider>
                      </MyDepedentAccessProvider>
                    </MemberRegisterProvider>
                  </AuthorizationDepedentProvider>
                </ForgotUserNameProvider>
              </AuthorizationDetailsProvider>
            </MyHomeTilesProvider>
          </ForgetPasswordProvider>
        </ChattestProvider>
      </UserLoginProvider>
    </AppProvider>
  );
};
